import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Flex, Tabs } from 'antd';
import { collect } from 'collect.js';
import { PrinterOutlined } from '@ant-design/icons';
import useHistory from '../../../../hooks/navigation/useHistory';
import useApiClient from '../../../../hooks/api/useApiClient';
import {
  DoorStickerTemplate,
  FlyerTemplate,
  PolygraphyProductType,
  StickerTemplate,
} from '../../../../services/exports/Constants';
import LoadingScreen from '../../../../components/loading/LoadingScreen';
import useHelpers from '../../../../hooks/context/useHelpers';
import ProductDetails from '../../../../components/marketing/polygraphy/ProductDetails';
import CustomPolygraphyProducts from '../../../../components/marketing/polygraphy/CustomPolygraphyProducts';
import AutoFlyerSettings from '../../../../components/marketing/polygraphy/AutoFlyerSettings';
import { ScreenEnum } from '../../../../hooks/navigation/Screens';

export const Tab = {
  edit: 'edit',
  order: 'order',
};

function Content({ type, data, onProductUpdated }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Marketing:Polygraphy:Products' });

  const templates =
    {
      [PolygraphyProductType.Flyer]: Object.values(FlyerTemplate),
      [PolygraphyProductType.DoorSticker]: Object.values(DoorStickerTemplate),
      [PolygraphyProductType.Sticker]: Object.values(StickerTemplate),
    }[type] ?? [];

  const { getActiveTab, setActiveTab } = useHelpers();
  const activeTab = getActiveTab(`polygraphy_products.${type}`, templates[0] ?? null);

  const tabs = useMemo(
    () =>
      collect(templates)
        .map((item) => ({
          template: item,
          data: collect(data).firstWhere('template', item),
        }))
        .map((item) => ({
          key: item.template,
          label: t(`types.${type}.templates.${item.template}`),
          children: (
            <ProductDetails type={type} template={item.template} data={item.data} onProductUpdated={onProductUpdated} />
          ),
        }))
        .toArray(),
    [type, data],
  );

  if (type === PolygraphyProductType.Custom) {
    return <CustomPolygraphyProducts data={data} onProductSaved={onProductUpdated} />;
  }

  return (
    <Tabs
      activeKey={activeTab}
      onChange={(tab) => setActiveTab(tab, `polygraphy_products.${type}`)}
      tabBarExtraContent={{
        left: <h6 className="mr-md">{t(`types.${type}.name`)}</h6>,
      }}
      items={tabs}
    />
  );
}

export default function PolygraphyProductsView() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Marketing:Polygraphy:Products' });

  const { PolygraphyManager } = useApiClient();

  const history = useHistory();
  const { getActiveTab, setActiveTab } = useHelpers();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const activeTab = getActiveTab('polygraphy_product_type', PolygraphyProductType.Flyer);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    const { success, data: responseData } = await PolygraphyManager.getProducts();
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    setProducts(responseData);
  }

  const onProductSaved = (data) =>
    setProducts((current) => {
      const index = collect(current).search((item) => item.id === data.id);

      if (index !== false) {
        return collect(current).put(index, data).toArray();
      }

      return [...current, data];
    });

  const tabs = useMemo(
    () =>
      collect(Object.values(PolygraphyProductType))
        .map((item) => ({
          type: item,
          data: collect(products).where('type', item).toArray(),
        }))
        .map((item) => ({
          key: item.type,
          label: t(`types.${item.type}.name`, {
            defaultValue: item.type === PolygraphyProductType.DoorSticker ? 'Door Sticker' : item.type,
          }),
          children: <Content type={item.type} data={item.data} onProductUpdated={onProductSaved} />,
        }))
        .toArray(),
    [products],
  );

  return (
    <>
      {loading && <LoadingScreen />}
      {!loading && (
        <>
          <Flex justify="space-between">
            <h3 className="mb-md my-auto">{t('title')}</h3>
            <Button
              color="default"
              variant="solid"
              icon={<PrinterOutlined />}
              onClick={() => history.push(ScreenEnum.MarketingPolygraphyOrders)}
            >
              {t('buttons.order')}
            </Button>
          </Flex>
          <AutoFlyerSettings products={products} />
          <Tabs
            type="card"
            activeKey={activeTab}
            onChange={(tab) => setActiveTab(tab, 'polygraphy_product_type')}
            items={tabs}
          />
        </>
      )}
    </>
  );
}
